/** Shared Contact Manager helpers (lists recipients + import). */

import { PROMOTE_PIPELINE_CHIPS } from '../services/contactPromotePipelineService';
import { isSystemSegmentList } from '../services/segmentTypes';
import {
  ALL_CONTACTS_LIST_ID,
  ACTIVE_DB_LIST_ID,
  INACTIVE_DB_LIST_ID,
  RECENT_CUSTOMERS_LIST_ID,
} from '../services/segmentAudienceConstants';
import { formatMembershipMetaBrief, formatTradeFilterSummary } from './contactAccountSignals';

export { isSystemSegmentList };
export { ALL_CONTACTS_LIST_ID, ACTIVE_DB_LIST_ID, INACTIVE_DB_LIST_ID, RECENT_CUSTOMERS_LIST_ID };

export const LISTS_COLL = 'lists';
/** Fixed id for trade account applications from the public Accounts form (Cloud Run appends rows). */
export const ACCOUNT_REQUESTS_LIST_ID = 'account_requests';
/** Virtual Contact Manager list backed by `marketing_unsubscribe_requests` (read-only in UI). */
export const MARKETING_UNSUBSCRIBES_LIST_ID = '__marketing_unsubscribes__';
export const USERS_COLL = 'users';

/** Virtual sidebar lists (not Firestore docs) — no row writes, no list metadata edit. */
export function isContactManagerVirtualListId(id) {
  return (
    id === ALL_CONTACTS_LIST_ID ||
    id === ACTIVE_DB_LIST_ID ||
    id === INACTIVE_DB_LIST_ID ||
    id === RECENT_CUSTOMERS_LIST_ID ||
    id === MARKETING_UNSUBSCRIBES_LIST_ID
  );
}

/**
 * Writable manual Firestore list in Contact Manager (not virtual, not system segment).
 * @param {string|object} listOrId
 * @param {object} [listData]
 */
export function isContactManagerFirestoreListEditable(listOrId, listData) {
  const id = typeof listOrId === 'string' ? listOrId : listOrId?.id;
  if (!id || isContactManagerVirtualListId(id)) return false;
  if (typeof listOrId === 'object' && listOrId && isSegmentListReadOnly(listOrId)) return false;
  if (listData && isSegmentListReadOnly(listData)) return false;
  return true;
}

/** System behavioural segment lists — read-only in Contact Manager (view, export, queue to editor). */
export function isSegmentListReadOnly(idOrList, listData) {
  if (idOrList && typeof idOrList === 'object') {
    return isSystemSegmentList(idOrList);
  }
  if (listData) return isSystemSegmentList(listData);
  return false;
}

/** Virtual / system lists where row edit, import, and pipeline edit are blocked. */
export function isNonWritableContactManagerList(list) {
  if (!list) return true;
  if (isContactManagerVirtualListId(list.id)) return true;
  if (isReadonlyContactManagerList(list.id)) return true;
  if (isSegmentListReadOnly(list)) return true;
  return false;
}

/** Virtual lists: no direct recipient writes from Contact Manager (import, add row, etc.). */
export function isReadonlyContactManagerList(id) {
  return id === ALL_CONTACTS_LIST_ID || id === MARKETING_UNSUBSCRIBES_LIST_ID;
}

/**
 * True when the sidebar list may have rows removed after a transfer or “create and peel” (writable Firestore list only).
 */
export function sidebarListAllowsRecipientRemoval(list) {
  if (!list) return false;
  if (isContactManagerVirtualListId(list.id)) return false;
  if (isReadonlyContactManagerList(list.id)) return false;
  if (isSegmentListReadOnly(list)) return false;
  return true;
}

/**
 * Preview removing source rows whose email appears in rowsWithEmail (normalized). Used before Firestore writes + confirm copy.
 */
export function previewRemoveFromSourceByEmails(sourceRecipients, rowsWithEmail) {
  const drop = new Set(
    (rowsWithEmail || [])
      .map((r) => toLower(r?.email || r?.Email))
      .filter(Boolean),
  );
  const src = sourceRecipients || [];
  const nextRecipients = src.filter((rec) => !drop.has(toLower(rec?.email || rec?.Email)));
  return {
    nextRecipients,
    removedCount: src.length - nextRecipients.length,
    distinctEmailsInPayload: drop.size,
  };
}

export const clean = (v) => (v == null ? '' : String(v).trim());
export const toLower = (v) => clean(v).toLowerCase();

/**
 * Lowercase email keys for rows on the Account requests list (trade applications from the public Accounts form).
 * @param {{ id: string, recipients?: object[] }[]} lists
 * @returns {Set<string>}
 */
export function buildAccountRequestEmailSet(lists) {
  const list = (lists || []).find((l) => l && l.id === ACCOUNT_REQUESTS_LIST_ID);
  const set = new Set();
  for (const r of list?.recipients || []) {
    const em = toLower(r?.email || r?.Email);
    if (em) set.add(em);
  }
  return set;
}

const MIN_PROMOTE_COMPANY_LEN = 4;
const MIN_PROMOTE_NAME_LEN = 4;
const PROMOTE_HINT_CAP = 12;

function normPromoteCompany(rec) {
  const raw = clean(rec?.company || rec?.['Company Name'] || '');
  return toLower(raw.replace(/\s+/g, ' ').trim());
}

function normPromotePerson(rec) {
  const fn = clean(rec?.firstName || rec?.['First Name'] || '');
  const ln = clean(rec?.lastName || rec?.['Last Name'] || '');
  return toLower(`${fn} ${ln}`.replace(/\s+/g, ' ').trim());
}

function briefPromoteRow(row) {
  const n = [row?.firstName || row?.['First Name'], row?.lastName || row?.['Last Name']].filter(Boolean).join(' ').trim();
  const c = row?.company || row?.['Company Name'] || '';
  const parts = [n, c].filter(Boolean);
  return parts.length ? parts.join(', ') : row?.email || row?.Email || '';
}

/**
 * Cross-list hints before Contact Manager promote: same email elsewhere, same company, or same name+company.
 * @param {object} contact Row about to be promoted.
 * @param {Array<{ id: string, name?: string, recipients?: object[] }>} lists Firestore `lists` snapshot (not virtual ALL Contacts).
 * @param {{ selfListId?: string|null, selfRecipientId?: string }} [options]
 */
export function findPotentialPromoteDuplicateHints(contact, lists, options = {}) {
  const sameEmailElsewhere = [];
  const sameCompanyOther = [];
  const sameNameAndCompanyOther = [];
  const selfEmail = toLower(contact?.email || contact?.Email || '');
  const selfListId = options.selfListId || '';
  const selfRecipientId = contact?.id || '';
  if (!selfEmail) {
    return { sameEmailElsewhere, sameCompanyOther, sameNameAndCompanyOther };
  }
  const companySelf = normPromoteCompany(contact);
  const nameSelf = normPromotePerson(contact);
  const seenEmailKey = new Set();
  const seenCompanyEmail = new Set();
  const seenNameEmail = new Set();

  for (const list of lists || []) {
    if (!list || !list.id || !Array.isArray(list.recipients)) continue;
    const listId = list.id;
    const listName = list.name || listId;
    for (const row of list.recipients) {
      const em = toLower(row?.email || row?.Email || '');
      if (!em) continue;
      const rowId = row?.id || '';
      if (listId === selfListId && rowId && selfRecipientId && rowId === selfRecipientId && em === selfEmail) {
        continue;
      }
      if (em === selfEmail) {
        const k = `${listId}:${rowId || em}`;
        if (seenEmailKey.has(k)) continue;
        seenEmailKey.add(k);
        sameEmailElsewhere.push({ listId, listName, email: em, brief: briefPromoteRow(row) });
        continue;
      }
      if (companySelf.length >= MIN_PROMOTE_COMPANY_LEN) {
        const cn = normPromoteCompany(row);
        if (!cn || cn !== companySelf) continue;
        const isNameCompany =
          nameSelf.length >= MIN_PROMOTE_NAME_LEN &&
          normPromotePerson(row) === nameSelf &&
          cn === companySelf;
        if (isNameCompany) {
          const nk = `${listId}:${em}`;
          if (!seenNameEmail.has(nk)) {
            seenNameEmail.add(nk);
            sameNameAndCompanyOther.push({ listId, listName, email: em, brief: briefPromoteRow(row) });
          }
          continue;
        }
        const ck = `${listId}:${em}`;
        if (!seenCompanyEmail.has(ck)) {
          seenCompanyEmail.add(ck);
          sameCompanyOther.push({ listId, listName, email: em, brief: briefPromoteRow(row) });
        }
      }
    }
  }

  return {
    sameEmailElsewhere: sameEmailElsewhere.slice(0, PROMOTE_HINT_CAP),
    sameCompanyOther: sameCompanyOther.slice(0, PROMOTE_HINT_CAP),
    sameNameAndCompanyOther: sameNameAndCompanyOther.slice(0, PROMOTE_HINT_CAP),
  };
}

/**
 * Plain-text block for window.confirm before promote (London Smoke & Cure staff).
 * @param {{ sameEmailElsewhere: object[], sameCompanyOther: object[], sameNameAndCompanyOther: object[] }} hints
 * @returns {string} empty if nothing to show
 */
export function formatPromotePrecheckMessage(hints) {
  if (!hints) return '';
  const lines = [];
  const pushCap = (title, arr) => {
    if (!arr?.length) return;
    lines.push(title);
    arr.forEach((x) => {
      lines.push(`  - ${x.listName}: ${x.email} (${x.brief})`);
    });
  };
  pushCap('Same email also appears on another list (check for duplicate rows):', hints.sameEmailElsewhere);
  pushCap('Same company name, different email (possible same business):', hints.sameCompanyOther);
  pushCap('Same person name and company, different email (possible duplicate person):', hints.sameNameAndCompanyOther);
  return lines.join('\n');
}

export const slugify = (str) =>
  (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .substring(0, 60);
export const uniqueId = () => Math.random().toString(36).slice(2, 10);

const TRUTHY = new Set(['x', 'X', 'yes', 'Yes', 'YES', 'true', 'True', '1']);

/** Column-style chip label that is really a phone field (value is phone digits). */
const PHONE_COLUMN_CHIP_PREFIX =
  /^(mobile|phone|tel|cell|telephone|cellphone|contact\s*number|phone\s*number|mobile\s*number|work\s*phone|home\s*phone|direct\s*line|fax|main\s*line|switchboard)\s*:\s*(.+)$/i;

function digitCount(s) {
  return (String(s).match(/\d/g) || []).length;
}

/**
 * True if a tag string should be treated as a phone number, not a segment chip (company type, sector, etc.).
 */
export function isPhoneLikeChip(chip) {
  const c = clean(chip);
  if (!c) return false;
  const m = c.match(PHONE_COLUMN_CHIP_PREFIX);
  if (m) {
    const val = clean(m[2]);
    return digitCount(val) >= 8;
  }
  if (/^[\d\s\-+().]+$/.test(c)) {
    const d = digitCount(c);
    return d >= 10 && d <= 15;
  }
  return false;
}

function extractPhoneFromPhoneLikeChip(c) {
  const s = clean(c);
  const m = s.match(PHONE_COLUMN_CHIP_PREFIX);
  if (m) return clean(m[2]);
  if (/^[\d\s\-+().]+$/.test(s) && digitCount(s) >= 10) return s;
  return '';
}

/** Import-style `Address: full street…` — not a segment tag; use Address + City fields (city can still be tagged separately). */
const ADDRESS_COLUMN_CHIP_PREFIX =
  /^(address|street|full address|delivery address|billing address|postal address|shipping address|registered address|business address|address line 1|address line 2)\s*:\s*(.+)$/i;

/**
 * True if a tag is really a full/postal address line, not a segment (e.g. sector, city tag).
 */
export function isAddressLikeChip(chip) {
  const c = clean(chip);
  if (!c) return false;
  const m = c.match(ADDRESS_COLUMN_CHIP_PREFIX);
  if (!m) return false;
  const val = clean(m[2]);
  if (val.length < 12) return false;
  if (/^(n\/a|na|none|tbc|-|–)$/i.test(val)) return false;
  return true;
}

function extractAddressFromAddressLikeChip(c) {
  const m = clean(c).match(ADDRESS_COLUMN_CHIP_PREFIX);
  return m ? clean(m[2]) : '';
}

/**
 * Removes phone-like and full-address entries from `chips`.
 * Fills `phone` / `address` from the first matching chip when those fields are empty.
 */
export function migratePhoneAndAddressOutOfChips(chips, phoneSoFar = '', addressSoFar = '') {
  let phone = clean(phoneSoFar);
  let address = clean(addressSoFar);
  const out = [];
  for (const raw of chips || []) {
    const c = clean(typeof raw === 'string' ? raw : '');
    if (!c) continue;
    if (isPhoneLikeChip(c)) {
      if (!phone) {
        const extracted = extractPhoneFromPhoneLikeChip(c);
        if (extracted) phone = extracted;
      }
      continue;
    }
    if (isAddressLikeChip(c)) {
      if (!address) {
        const extracted = extractAddressFromAddressLikeChip(c);
        if (extracted) address = extracted;
      }
      continue;
    }
    out.push(c);
  }
  return { chips: out, phone, address };
}

/**
 * Removes phone-like entries from `chips`; keeps `phone` field as the single place for numbers.
 * If `phone` is empty, fills it from the first extracted phone-like chip.
 */
export function migratePhoneOutOfChips(chips, phoneSoFar = '') {
  const r = migratePhoneAndAddressOutOfChips(chips, phoneSoFar, '');
  return { chips: r.chips, phone: r.phone };
}

/** Tags shown in lists and filters — excludes phone- and full-address-like strings (use Phone / Address / City fields). */
export function segmentChipsForDisplay(chips) {
  return (Array.isArray(chips) ? chips : [])
    .map((c) => clean(typeof c === 'string' ? c : ''))
    .filter(Boolean)
    .filter((c) => !isPhoneLikeChip(c) && !isAddressLikeChip(c));
}

/** Case-insensitive column names that map to core recipient fields (no auto-chip; stripped from passthrough). */
const IMPORT_IDENTITY_KEYS_LOWER = new Set([
  'email',
  'e-mail',
  'email address',
  'company',
  'company name',
  'company_name',
  'organization',
  'organisation',
  'first',
  'first name',
  'firstname',
  'first_name',
  'last',
  'last name',
  'lastname',
  'last_name',
  'phone',
  'phone number',
  'phone no',
  'phone no.',
  'phone #',
  'mobile',
  'mobile number',
  'mobile no',
  'tel',
  'tel no',
  'telephone',
  'telephone number',
  'cell',
  'cell phone',
  'cell number',
  'contact number',
  'contact phone',
  'work phone',
  'home phone',
  'primary phone',
  'secondary phone',
  'direct line',
  'main line',
  'city',
  'town',
  'region',
  'address',
  'street',
  'address line 1',
  'address line 2',
  'full address',
  'delivery address',
  'billing address',
  'shipping address',
  'registered address',
  'business address',
  'postal address',
  'postcode',
  'post code',
  'postal code',
  'zip',
  'zip code',
  'notes',
  'note',
  'comments',
  'job title',
  'jobtitle',
  'title',
  'position',
  'company website',
  'website',
  'url',
  'web site',
  'scanned at',
  'scanned_at',
  'scanned by',
  'scanned_by',
  'scanner',
  'score',
  'lead status',
  'status',
  'leadstatus',
  'stage history (json)',
  'stagehistoryjson',
  'stage history',
  'id',
  'createdat',
  'updatedat',
  'chips',
  'tags',
  'firstname',
  'lastname',
  'companywebsite',
  'scannedat',
  'scannedby',
  'jobtitle',
]);

/** Headers longer than this are treated as survey/metadata columns: stored on the row, not turned into chips. */
const IMPORT_CHIP_SKIP_MIN_HEADER_LEN = 40;

/** Question-style / survey columns — never auto-chip as `Long question: answer` (too noisy). */
function looksLikeSurveyOrQuestionColumn(key) {
  const k = clean(key);
  if (!k) return true;
  if (k.includes('?')) return true;
  const lo = k.toLowerCase();
  if (lo.length >= IMPORT_CHIP_SKIP_MIN_HEADER_LEN) return true;
  // Trade-show / form imports: "How…", "What…", "Which…", "Is your…", "Type of…"
  if (/^(how|what|which)\b/i.test(k)) return true;
  if (/^is your\b/i.test(k)) return true;
  if (/^describe\b/i.test(k)) return true;
  if (/^type of\b/i.test(k)) return true;
  return false;
}

function isImportIdentityKey(key) {
  const k = clean(key);
  if (!k) return true;
  return IMPORT_IDENTITY_KEYS_LOWER.has(k.toLowerCase());
}

function shouldSkipAutoChipForImportKey(key) {
  const k = clean(key);
  if (!k) return true;
  if (isImportIdentityKey(k)) return true;
  if (looksLikeSurveyOrQuestionColumn(k)) return true;
  return false;
}

/** Map "Is your …" column headers to short labels for derived chips. Order matters (specific before general). */
function shortLabelForIsYourHeader(header) {
  const h = clean(header).toLowerCase();
  if (!h.startsWith('is your')) return null;
  if (h.includes('coffee') || h.includes('café') || h.includes('cafe')) return 'Café';
  if (h.includes('distribution')) return 'Distribution';
  if (h.includes('events') || h.includes('leisure')) return 'Events';
  if (h.includes('hotel')) return 'Hotel';
  if (h.includes('pub')) return 'Pub';
  if (h.includes('restaurant')) return 'Restaurant';
  if (h.includes('business')) return 'Business';
  return 'Venue';
}

/**
 * Compact segment chips for trade-show CSVs (London Smoke style). Full survey answers stay on the row as fields.
 */
function deriveTradeShowSegmentChips(row, byLower) {
  const derived = [];
  const seen = new Set();

  const push = (chip) => {
    const c = clean(chip);
    if (!c || c.length > 120 || seen.has(c)) return;
    seen.add(c);
    derived.push(c);
  };

  const TRADE_SHOW_DERIVED = [
    {
      aliases: ['how many outlets/establishments does your company operate?'],
      prefix: 'Outlets',
    },
    {
      aliases: ['how would you describe your restaurant'],
      prefix: 'Restaurant',
    },
    {
      aliases: ['what is your company\'s main activity?', 'what is your companys main activity?'],
      prefix: 'Activity',
    },
    {
      aliases: ['what is your job function?'],
      prefix: 'Job',
    },
    {
      aliases: ['what is your job seniority?'],
      prefix: 'Seniority',
    },
    {
      aliases: ['what role do you play in the decision making process?'],
      prefix: 'Decision',
    },
    {
      aliases: [
        'which of the following best describes the sector in which you work?',
        'which of the following best describes the sector in which you work',
      ],
      prefix: 'Sector',
    },
    {
      aliases: ['type of caterer'],
      prefix: 'Caterer',
    },
  ];

  for (const { aliases, prefix } of TRADE_SHOW_DERIVED) {
    for (const a of aliases) {
      const e = byLower.get(a.toLowerCase());
      if (!e) continue;
      const val = clean(e.value);
      if (!val || /^(yes|no|n\/a|na)$/i.test(val)) continue;
      let display = val.length > 56 ? `${val.slice(0, 53)}…` : val;
      push(`${prefix}: ${display}`);
      break;
    }
  }

  for (const [k, raw] of Object.entries(row)) {
    const val = clean(raw);
    if (!val || /^(yes|no|y|n)$/i.test(val)) continue;
    const label = shortLabelForIsYourHeader(k);
    if (!label) continue;
    let display = val.length > 48 ? `${val.slice(0, 45)}…` : val;
    push(`${label}: ${display}`);
  }

  return derived;
}

function parseChipsField(chipsLike) {
  return Array.isArray(chipsLike)
    ? chipsLike.map(clean).filter(Boolean)
    : clean(chipsLike)
        .split(',')
        .map((c) => clean(c))
        .filter(Boolean);
}

function chipsFromArbitraryColumns(input) {
  const chips = [];
  for (const [key, rawVal] of Object.entries(input || {})) {
    if (shouldSkipAutoChipForImportKey(key)) continue;
    const val = clean(rawVal);
    if (!val) continue;
    const header = key.replace(/\s+/g, ' ').trim();
    if (TRUTHY.has(val)) chips.push(header);
    else chips.push(`${header}: ${val}`);
  }
  const out = [];
  const seen = new Set();
  for (const c of chips) if (!seen.has(c)) { seen.add(c); out.push(c); }
  return out;
}

/**
 * Lead exports often prepend funnel/status rows (Secured, Warm, etc.) before the real header.
 * Without trimming, `Papa.parse({ header: true })` uses the wrong first line as column names.
 * @param {string} csvText
 * @returns {string} Text from the detected header row onward, or the original if no match
 */
export function trimCsvUntilDataHeader(csvText) {
  if (csvText == null || typeof csvText !== 'string') return '';
  const lines = csvText.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const lower = lines[i].toLowerCase();
    if (
      lower.includes('email') &&
      lower.includes('scanned at') &&
      (lower.includes('first name') || lower.includes('phone'))
    ) {
      return lines.slice(i).join('\n');
    }
  }
  return csvText;
}

/**
 * Normalizes a CSV row (any header casing) into a shape `normalizeRecipient` understands.
 * Trade-show / lead scanners: maps "First name", "Scanned by", "Mobile", etc.; adds chip `Scanned by: {name}` for filtering.
 * Survey-style columns stay as plain fields (long headers are not auto-chipped).
 */
export function normalizeImportRow(rawRow) {
  if (!rawRow || typeof rawRow !== 'object') return {};
  const row = {};
  for (const [k, v] of Object.entries(rawRow)) {
    const nk = clean(k);
    if (!nk) continue;
    row[nk] = v;
  }
  const byLower = new Map();
  for (const [k, v] of Object.entries(row)) {
    byLower.set(k.toLowerCase(), { key: k, value: v });
  }
  const consumed = new Set();

  function take(aliases) {
    for (const a of aliases) {
      const e = byLower.get(a.toLowerCase());
      if (e && clean(e.value)) {
        consumed.add(e.key);
        return clean(e.value);
      }
    }
    return '';
  }

  let mobile = '';
  for (const a of ['mobile', 'cell']) {
    const e = byLower.get(a);
    if (e && clean(e.value)) {
      consumed.add(e.key);
      mobile = clean(e.value);
      break;
    }
  }
  let phoneCol = '';
  for (const a of ['phone', 'tel', 'telephone']) {
    const e = byLower.get(a);
    if (e && clean(e.value)) {
      consumed.add(e.key);
      phoneCol = clean(e.value);
      break;
    }
  }
  const phoneCombined = mobile || phoneCol;

  const email = take(['email', 'e-mail', 'email address']);
  const first = take(['first name', 'firstname', 'first_name', 'first']);
  const last = take(['last name', 'lastname', 'last_name', 'last']);
  const company = take([
    'company',
    'company name',
    'company_name',
    'organization',
    'organisation',
  ]);
  const address = take([
    'address',
    'street',
    'address line 1',
    'full address',
    'delivery address',
    'billing address',
    'shipping address',
  ]);
  const notesMain = take(['notes', 'note', 'comments']);
  const commentsSecond = take(['comments 2', 'comments_2', 'comments2']);
  const notes = [notesMain, commentsSecond].filter(Boolean).join('\n\n') || '';
  const jobTitle = take(['job title', 'jobtitle', 'title', 'position']);
  const companyWebsite = take([
    'company website',
    'website',
    'url',
    'web site',
  ]);
  const scannedAt = take(['scanned at', 'scanned_at']);
  const scannedBy = take(['scanned by', 'scanned_by', 'scanner']);
  const score = take(['score', 'lead score']);
  const city = take(['city', 'town', 'region']);
  const leadStageCol = take([
    'lead status',
    'leadstatus',
    'lead stage',
    'lead_stage',
    'pipeline stage',
    'pipeline_stage',
    'crm stage',
    'list stage',
    'list_stage',
    'stage id',
    'stage_id',
    'stageid',
    'stage',
    'status',
  ]);

  const out = {};
  if (email) out.email = email;
  if (company) out['Company Name'] = company;
  if (first) out['First Name'] = first;
  if (last) out['Last Name'] = last;
  if (phoneCombined) out.phone = phoneCombined;
  if (address) out.address = address;
  if (notes) out.notes = notes;
  if (jobTitle) out.jobTitle = jobTitle;
  if (companyWebsite) out['Company Website'] = companyWebsite;
  if (scannedAt) out.scannedAt = scannedAt;
  if (scannedBy) out.scannedBy = scannedBy;
  if (score) out.score = score;
  if (city) out.city = city;
  if (leadStageCol) out.leadStatus = leadStageCol;

  const derivedSegmentChips = deriveTradeShowSegmentChips(row, byLower);
  if (scannedBy) {
    out.chips = [...(out.chips || []), `Scanned by: ${scannedBy}`];
  }
  if (derivedSegmentChips.length) {
    out.chips = [...(out.chips || []), ...derivedSegmentChips];
  }

  for (const [k, v] of Object.entries(row)) {
    if (consumed.has(k)) continue;
    const kl = k.toLowerCase();
    if (kl === 'chips' || kl === 'tags') {
      const parsed = parseChipsField(v);
      if (parsed.length) out.chips = [...(out.chips || []), ...parsed];
      continue;
    }
    out[k] = v;
  }

  if (Array.isArray(out.chips) && out.chips.length) {
    out.chips = [...new Set(out.chips.map((c) => clean(c)).filter(Boolean))];
  }
  const migratedImp = migratePhoneAndAddressOutOfChips(out.chips || [], out.phone || '', out.address || '');
  out.chips = migratedImp.chips;
  if (migratedImp.phone) out.phone = migratedImp.phone;
  if (migratedImp.address) out.address = migratedImp.address;
  return out;
}

/** Chips that promotion / account flows must not drop when editing a list row (user can still add others). */
export function getPromotionLockedChipsSet() {
  return new Set([
    'Has Account',
    'Account: Active',
    'Account: Inactive',
    ...Object.values(PROMOTE_PIPELINE_CHIPS),
  ]);
}

/**
 * Remove `undefined` recursively so Firestore `updateDoc` / `setDoc` never rejects.
 * Preserves Firestore Timestamp-like objects and Date.
 */
export function stripUndefinedDeep(value, depth = 0) {
  if (depth > 40) return value;
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== 'object') return value;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function' && typeof value.seconds === 'number') return value;
  if (Array.isArray(value)) {
    return value
      .map((v) => stripUndefinedDeep(v, depth + 1))
      .filter((v) => v !== undefined);
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (v === undefined) continue;
    const next = stripUndefinedDeep(v, depth + 1);
    if (next !== undefined) out[k] = next;
  }
  return out;
}

/** When the editor sends empty `notes`, remove legacy comment keys so list saves do not resurrect old text. */
function stripLegacyInternalNoteFieldsWhenCleared(merged) {
  if (!merged || typeof merged !== 'object') return;
  delete merged.Notes;
  delete merged.Comments;
  delete merged.comments;
  delete merged['Comments 2'];
  delete merged['comments 2'];
  delete merged.comments_2;
  merged.notes = '';
}

/**
 * Merge form-normalized row with the previous Firestore recipient: keep promotion/account chips,
 * align account status from `users` when `accountInfo` is set, preserve `stageHistory` unless
 * `norm` supplies a new one, and strip undefined for Firestore.
 *
 * @param {object} norm - from `normalizeRecipient`
 * @param {object|null|undefined} oldRec - previous row or null for new
 * @param {{ has?: boolean, active?: boolean }|null|undefined} accountInfo - from `users` overlay
 */
export function mergeRecipientWithPreviousForSave(norm, oldRec, accountInfo) {
  if (!norm) return null;
  const old = oldRec || {};
  const merged = { ...old, ...norm };
  merged.id = old.id || norm.id;
  const locked = getPromotionLockedChipsSet();
  const prevChips = Array.isArray(old.chips) ? old.chips : [];
  const nextChips = Array.isArray(norm.chips) ? norm.chips : [];
  const lockedFromPrev = prevChips.filter((c) => locked.has(c));
  let chips = Array.from(new Set([...nextChips, ...lockedFromPrev]));
  if (accountInfo && accountInfo.has === true) {
    chips = chips.filter((c) => c !== 'Account: Active' && c !== 'Account: Inactive');
    chips = Array.from(
      new Set([
        ...chips,
        'Has Account',
        accountInfo.active ? 'Account: Active' : 'Account: Inactive',
      ]),
    );
  }
  merged.chips = chips;
  merged.stageHistory = norm.stageHistory !== undefined ? norm.stageHistory : old.stageHistory;
  merged.email = norm.email || merged.email;
  merged.Email = norm.Email || merged.Email || merged.email;
  if (Object.prototype.hasOwnProperty.call(norm, 'notes') && !clean(norm.notes)) {
    stripLegacyInternalNoteFieldsWhenCleared(merged);
  }
  return stripUndefinedDeep(merged);
}

/**
 * Normalize company text for use as a single segment chip (trim, collapse spaces, cap length).
 * @param {string|null|undefined} company
 * @returns {string|null}
 */
export function extractCompanyChip(company) {
  const c = clean(company);
  if (!c) return null;
  const normalized = String(c).replace(/\s+/g, ' ').trim();
  if (!normalized) return null;
  return normalized.length > 120 ? normalized.slice(0, 120).trim() : normalized;
}

const ACCOUNT_CHIP_LABELS_LOWER = new Set(
  ['has account', 'account: active', 'account: inactive', 'additional contact'],
);

const ONBOARDING_CHIP_LABELS_LOWER = new Set(
  Object.values(PROMOTE_PIPELINE_CHIPS).map((s) => String(s).trim().toLowerCase()),
);

function chipMatchesExtractedLabel(chip, extracted) {
  if (!extracted || !chip) return false;
  return clean(chip).toLowerCase() === clean(extracted).toLowerCase();
}

/**
 * Trade-show / import chips use `Prefix: value` (see `deriveTradeShowSegmentChips`). Buckets match
 * spreadsheet themes (sector, role, venue type, etc.) so the UI is not one flat "Segments" list.
 * Order = default display order.
 */
const TRADE_SEGMENT_BUCKET_DEFS = [
  { id: 'seg-outlets', label: 'Outlets & scale', test: (s) => /^outlets:\s*/i.test(s) },
  { id: 'seg-activity', label: 'Activities', test: (s) => /^activity:\s*/i.test(s) },
  { id: 'seg-sector', label: 'Sector', test: (s) => /^sector:\s*/i.test(s) },
  { id: 'seg-role', label: 'Role & decisions', test: (s) => /^(job|seniority|decision):\s*/i.test(s) },
  {
    id: 'seg-venue',
    label: 'Venue & channel',
    test: (s) => /^(café|cafe|distribution|events|hotel|pub|business|venue):\s*/i.test(s),
  },
  { id: 'seg-restaurant', label: 'Restaurant profile', test: (s) => /^restaurant:\s*/i.test(s) },
  { id: 'seg-caterer', label: 'Catering type', test: (s) => /^caterer:\s*/i.test(s) },
  { id: 'seg-scanned', label: 'Lead capture', test: (s) => /^scanned by:\s*/i.test(s) },
  {
    id: 'seg-followup',
    label: 'Follow-up & calls',
    test: (s) => /^_\d+:\s*/.test(s) || /^called:/i.test(s),
  },
];

function isLongFormSurveyAnswerChip(chip) {
  const i = String(chip).indexOf(':');
  if (i <= 0) return false;
  const head = String(chip).slice(0, i).trim();
  if (head.length < 28) return false;
  return (
    head.includes('?') ||
    /^(how|what|which)\s/i.test(head) ||
    /^is your\b/i.test(head) ||
    /^type of\b/i.test(head) ||
    /^describe\b/i.test(head)
  );
}

function classifyTradeShowStyleChip(chip) {
  const s = String(chip);
  for (const def of TRADE_SEGMENT_BUCKET_DEFS) {
    if (def.test(s)) return def;
  }
  if (isLongFormSurveyAnswerChip(s)) {
    return { id: 'seg-form-qa', label: 'Form answers' };
  }
  return null;
}

/**
 * Group segment chips for UI (table, quick view, chip editor). Storage stays a flat `chips[]`.
 * Uses Company / City on the contact row to separate auto-style company vs Maps/locality chips when they match.
 *
 * @param {string[]|unknown} chips
 * @param {object} [ctx] Recipient-like: `company`, `Company Name`, `city`, `City`
 * @returns {{ id: string, label: string, chips: string[] }[]}
 */
export function groupSegmentChipsForDisplay(chips, ctx = {}) {
  const company =
    clean(ctx.company) ||
    clean(ctx.Company) ||
    clean(ctx['Company Name']) ||
    '';
  const city =
    clean(ctx.city) ||
    clean(ctx.City) ||
    '';
  const coChip = extractCompanyChip(company);
  const cityChip = extractCompanyChip(city);

  const flat = segmentChipsForDisplay(chips);
  const account = [];
  const onboarding = [];
  const compliance = [];
  const companyGroup = [];
  const location = [];
  const domain = [];
  const segments = [];
  /** @type {Record<string, string[]>} */
  const tradeBuckets = { 'seg-form-qa': [] };
  for (const def of TRADE_SEGMENT_BUCKET_DEFS) {
    tradeBuckets[def.id] = [];
  }

  for (const raw of flat) {
    const chip = raw;
    const lo = chip.toLowerCase();
    if (ACCOUNT_CHIP_LABELS_LOWER.has(lo)) {
      account.push(chip);
      continue;
    }
    if (ONBOARDING_CHIP_LABELS_LOWER.has(lo)) {
      onboarding.push(chip);
      continue;
    }
    if (chip === 'DNC' || /^unsubscribed/i.test(chip)) {
      compliance.push(chip);
      continue;
    }
    if (coChip && chipMatchesExtractedLabel(chip, coChip)) {
      companyGroup.push(chip);
      continue;
    }
    if (cityChip && chipMatchesExtractedLabel(chip, cityChip)) {
      location.push(chip);
      continue;
    }
    if (chip.startsWith('@') && chip.length > 1) {
      domain.push(chip);
      continue;
    }
    const trade = classifyTradeShowStyleChip(chip);
    if (trade) {
      const bucket = tradeBuckets[trade.id];
      if (bucket) bucket.push(chip);
      else segments.push(chip);
      continue;
    }
    segments.push(chip);
  }

  /** @type { { id: string, label: string, chips: string[] }[] } */
  const order = [
    { id: 'account', label: 'Account', chips: account },
    { id: 'onboarding', label: 'Onboarding', chips: onboarding },
    { id: 'compliance', label: 'Email and consent', chips: compliance },
    { id: 'company', label: 'Company', chips: companyGroup },
    { id: 'location', label: 'Area', chips: location },
    { id: 'domain', label: 'Email domain', chips: domain },
    ...TRADE_SEGMENT_BUCKET_DEFS.map((def) => ({
      id: def.id,
      label: def.label,
      chips: tradeBuckets[def.id],
    })),
    { id: 'seg-form-qa', label: 'Form answers', chips: tradeBuckets['seg-form-qa'] },
    { id: 'segments', label: 'Other tags', chips: segments },
  ];
  return order.filter((g) => g.chips.length > 0);
}

/** Display order when aggregating the chip cloud; lower rank wins if a label appears under different groups on different rows. */
const CHIP_CLOUD_CATEGORY_ORDER = [
  'account',
  'onboarding',
  'compliance',
  'company',
  'location',
  'domain',
  'seg-outlets',
  'seg-activity',
  'seg-sector',
  'seg-role',
  'seg-venue',
  'seg-restaurant',
  'seg-caterer',
  'seg-scanned',
  'seg-followup',
  'seg-form-qa',
  'segments',
];
const CHIP_CLOUD_CATEGORY_RANK = Object.fromEntries(
  CHIP_CLOUD_CATEGORY_ORDER.map((id, i) => [id, i]),
);

/**
 * Bucket unique segment tags for the Contact Manager flat-list chip cloud using the same rules as
 * `groupSegmentChipsForDisplay`, aggregated across all `recipients`. If the same string is grouped
 * differently on different rows (e.g. company match vs generic segment), the stronger category wins
 * (account → … → segments).
 *
 * @param {object[]} recipients
 * @returns {{ id: string, label: string, chips: string[] }[]}
 */
export function groupChipCloudLabelsByCategory(recipients) {
  /** @type {Map<string, { id: string, label: string, rank: number }>} */
  const best = new Map();
  for (const r of recipients || []) {
    if (!r || typeof r !== 'object') continue;
    const groups = groupSegmentChipsForDisplay(r.chips || [], r);
    for (const g of groups) {
      const rank = CHIP_CLOUD_CATEGORY_RANK[g.id] ?? 99;
      for (const c of g.chips) {
        const prev = best.get(c);
        if (!prev || rank < prev.rank) {
          best.set(c, { id: g.id, label: g.label, rank });
        }
      }
    }
  }
  /** @type {Map<string, { id: string, label: string, chips: string[] }>} */
  const byId = new Map();
  for (const [chip, meta] of best) {
    if (!byId.has(meta.id)) {
      byId.set(meta.id, { id: meta.id, label: meta.label, chips: [] });
    }
    byId.get(meta.id).chips.push(chip);
  }
  /** @type {{ id: string, label: string, chips: string[] }[]} */
  const out = [];
  for (const id of CHIP_CLOUD_CATEGORY_ORDER) {
    const g = byId.get(id);
    if (g?.chips.length) {
      g.chips.sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }));
      out.push(g);
    }
  }
  return out;
}

/**
 * Split the flat-list chip cloud "Segments" bucket into scannable sub-groups (Activities, follow-up
 * notes, everything else). Used only for display; storage stays a flat `chips[]`.
 * When only one kind is present, returns a single row with `label: ''` so the parent "Segments"
 * heading is enough.
 *
 * @param {string[]} chips
 * @returns {{ id: string, label: string, chips: string[] }[]}
 */
export function subSplitSegmentChipsForCloud(chips) {
  const activity = [];
  const followUp = [];
  const rest = [];
  for (const c of chips || []) {
    const s = String(c);
    if (/^activity:\s*/i.test(s)) activity.push(c);
    else if (/^_\d+:\s*/.test(s) || /^called:/i.test(s)) followUp.push(c);
    else rest.push(c);
  }
  const sort = (a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  activity.sort(sort);
  followUp.sort(sort);
  rest.sort(sort);
  /** @type {{ id: string, label: string, chips: string[] }[]} */
  const parts = [];
  if (activity.length) {
    parts.push({ id: 'seg-activity', label: 'Activities', chips: activity });
  }
  if (followUp.length) {
    parts.push({ id: 'seg-followup', label: 'Follow-up & calls', chips: followUp });
  }
  if (rest.length) {
    parts.push({ id: 'seg-other', label: 'Places & other tags', chips: rest });
  }
  if (parts.length === 0) {
    return [{ id: 'segments', label: '', chips: [] }];
  }
  if (parts.length === 1) {
    return [{ id: parts[0].id, label: '', chips: parts[0].chips }];
  }
  return parts;
}

/**
 * Firestore `lists` documents whose `recipients[]` include the given email (case-insensitive).
 * @param {{ id: string, name?: string, recipients?: object[] }[]} lists
 * @param {string} email
 * @returns {{ id: string, name: string }[]}
 */
export function firestoreListsContainingEmail(lists, email) {
  const em = toLower(email);
  if (!em) return [];
  const out = [];
  for (const list of lists || []) {
    if (!list?.id) continue;
    const recs = list.recipients || [];
    const hit = recs.find((r) => toLower(r?.email || r?.Email) === em);
    if (hit) {
      out.push({
        id: list.id,
        name: clean(list.name) || list.id,
        membershipWhy: formatMembershipMetaBrief(hit.membershipMeta),
      });
    }
  }
  out.sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }));
  return out;
}

/**
 * Consumer / generic mail hosts — used only by `extractDomainChip` (legacy / manual); we do not auto-tag domains on contacts.
 * Company names are auto-tagged via `extractCompanyChip` instead.
 */
const GENERIC_EMAIL_PROVIDER_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.fr',
  'yahoo.de',
  'ymail.com',
  'hotmail.com',
  'hotmail.co.uk',
  'outlook.com',
  'live.com',
  'live.co.uk',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'gmx.com',
  'gmx.de',
  'gmx.co.uk',
  'gmx.net',
  'mail.com',
  'tutanota.com',
  'tutanota.de',
  'fastmail.com',
  'hey.com',
  'btinternet.com',
  'virgin.net',
  'virginmedia.com',
  'sky.com',
  'talktalk.net',
  'blueyonder.co.uk',
]);

/**
 * @param {string} domainLower — host part of email, lowercased
 * @returns {boolean}
 */
export function isGenericEmailProviderDomain(domainLower) {
  if (!domainLower || typeof domainLower !== 'string') return true;
  const d = domainLower.trim().toLowerCase();
  if (GENERIC_EMAIL_PROVIDER_DOMAINS.has(d)) return true;
  for (const g of GENERIC_EMAIL_PROVIDER_DOMAINS) {
    if (d.endsWith(`.${g}`)) return true;
  }
  return false;
}

/**
 * Returns `@example.com` for corporate / custom domains only — never for generic webmail (Yahoo, Gmail, etc.).
 * Not used for automatic contact tagging; use `extractCompanyChip` for list segments.
 */
export function extractDomainChip(email) {
  if (!email || typeof email !== 'string' || !email.includes('@')) return null;
  const at = email.lastIndexOf('@');
  if (at < 0 || at === email.length - 1) return null;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
  if (!domain || isGenericEmailProviderDomain(domain)) return null;
  return `@${domain}`;
}

/** Normalise a header string for matching pipeline stage column aliases (spaces and underscores removed). */
function compactImportHeaderKey(key) {
  return String(key || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

const PIPELINE_STAGE_HEADER_ALIASES_COMPACT = new Set([
  'leadstatus',
  'leadstage',
  'pipelinestage',
  'crmstage',
  'liststage',
  'stageid',
  'stage',
]);

/**
 * Best-effort pipeline stage string from an import row (CSV, Console dump, or merged object).
 * Does not default to Lead when absent; combine with {@link lastPipelineTargetFromStageHistory} or a default in callers.
 */
export function resolveLeadStatusFromImportRow(input) {
  if (!input || typeof input !== 'object') return '';
  const directTry = [
    'leadStatus',
    'LeadStatus',
    'Lead Status',
    'lead_status',
    'Lead stage',
    'Lead Stage',
    'lead stage',
    'Pipeline stage',
    'Pipeline Stage',
    'pipeline_stage',
    'CRM stage',
    'Crm Stage',
    'List stage',
    'List Stage',
    'Stage ID',
    'stageId',
    'stage_id',
    'Stage',
    'stage',
  ];
  for (const k of directTry) {
    if (!Object.prototype.hasOwnProperty.call(input, k)) continue;
    const v = clean(input[k]);
    if (v) return v;
  }
  const s = clean(input.status);
  if (s) return s;
  for (const [k, val] of Object.entries(input)) {
    if (val === undefined || val === null) continue;
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) continue;
    const compact = compactImportHeaderKey(k);
    if (!PIPELINE_STAGE_HEADER_ALIASES_COMPACT.has(compact)) continue;
    const v = clean(val);
    if (v) return v;
  }
  return '';
}

/**
 * Last pipeline hop target from a CRM `stageHistory` array (Firestore Console dumps, list rows).
 */
export function lastPipelineTargetFromStageHistory(stageHistory) {
  if (!Array.isArray(stageHistory) || !stageHistory.length) return '';
  const last = stageHistory[stageHistory.length - 1];
  if (!last || typeof last !== 'object') return '';
  return (
    clean(last.to) ||
    clean(last.To) ||
    clean(last.TO) ||
    clean(last.label) ||
    clean(last.Label) ||
    clean(last.stage) ||
    clean(last.Stage) ||
    clean(last.name) ||
    clean(last.id) ||
    ''
  );
}

function cloneStageHistoryEntries(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((h) => (h && typeof h === 'object' ? { ...h } : h));
}

/** Prefer explicit JSON columns (CSV export round-trip), else Firestore-style `stageHistory` array. */
function resolveStageHistoryForImport(input) {
  if (!input || typeof input !== 'object') return undefined;
  const jsonColumnKeys = [
    'Stage history (JSON)',
    'stageHistoryJson',
    'stage history (json)',
    'Stage history',
  ];
  for (const ck of jsonColumnKeys) {
    const raw = input[ck];
    if (typeof raw !== 'string') continue;
    const s = clean(raw);
    if (!s) continue;
    try {
      const parsed = JSON.parse(s);
      if (!Array.isArray(parsed)) continue;
      return cloneStageHistoryEntries(parsed);
    } catch {
      /* ignore */
    }
  }
  if (Array.isArray(input.stageHistory)) return cloneStageHistoryEntries(input.stageHistory);
  return undefined;
}

export function normalizeRecipient(input, existingIdIfAny = '', autoTagCompany = true) {
  const email =
    clean(input.email) ||
    clean(input.Email) ||
    clean(input['Email Address']) ||
    clean(input['E-mail']) ||
    '';
  if (!email) return null;

  const company =
    clean(input.company) ||
    clean(input.Company) ||
    clean(input['Company Name']) ||
    '';

  const first =
    clean(input.firstName) ||
    clean(input['First Name']) ||
    clean(input['First name']) ||
    clean(input.first) ||
    clean(input.firstname) ||
    '';

  const last =
    clean(input.lastName) ||
    clean(input['Last Name']) ||
    clean(input['Last name']) ||
    clean(input.last) ||
    clean(input.lastname) ||
    '';

  const phone =
    clean(input.phone) ||
    clean(input.Phone) ||
    clean(input.tel) ||
    clean(input.mobile) ||
    clean(input.Mobile) ||
    '';

  const city =
    clean(input.city) ||
    clean(input.City) ||
    clean(input.town) ||
    clean(input.region) ||
    '';

  const notes = clean(
    input.notes || input.Notes || input.Comments || input.comments || input['Comments 2'] || '',
  );
  const address = clean(input.address || input.Address);
  const jobTitle = clean(input.jobTitle || input['Job title'] || input['Job Title']);
  const companyWebsite = clean(
    input.companyWebsite || input['Company Website'] || input.website || input.URL || input.Url,
  );
  const scannedAt = clean(input.scannedAt || input['Scanned at']);
  const scannedBy = clean(input.scannedBy || input['Scanned by']);
  const score = clean(input.score || input.Score);

  const stageHistoryResolved = resolveStageHistoryForImport(input);
  const explicitLead = clean(resolveLeadStatusFromImportRow(input));
  const histTail = clean(lastPipelineTargetFromStageHistory(stageHistoryResolved));
  const weakExplicit =
    !explicitLead ||
    explicitLead.toLowerCase() === 'lead' ||
    explicitLead.toLowerCase() === 'new';
  const leadStatus = (!weakExplicit ? explicitLead : '') || histTail || explicitLead || 'Lead';

  const explicitChips = parseChipsField(input.chips ?? input.Chips ?? input.tags);
  const autoChips = chipsFromArbitraryColumns(input);
  let chips = Array.from(new Set([...explicitChips, ...autoChips])).filter(Boolean);

  if (autoTagCompany) {
    const companyChip = extractCompanyChip(company);
    if (companyChip && !chips.includes(companyChip)) {
      chips.push(companyChip);
    }
  }

  const passthrough = { ...input };
  for (const k of Object.keys(passthrough)) {
    if (isImportIdentityKey(k)) delete passthrough[k];
  }

  const id = existingIdIfAny || `${toLower(email)}-${uniqueId()}`;

  const migrated = migratePhoneAndAddressOutOfChips(chips, phone, address);
  const chipsOut = migrated.chips;
  const phoneOut = migrated.phone || phone;
  const addressOut = migrated.address || address;

  return stripUndefinedDeep({
    ...passthrough,
    id,
    email,
    Email: email,
    company,
    'Company Name': company,
    firstName: first,
    'First Name': first,
    lastName: last,
    'Last Name': last,
    phone: phoneOut,
    notes,
    address: addressOut,
    jobTitle,
    companyWebsite,
    scannedAt,
    scannedBy,
    score,
    city,
    leadStatus,
    chips: chipsOut,
    ...(stageHistoryResolved !== undefined ? { stageHistory: stageHistoryResolved } : {}),
  });
}

/** Same resolution as Contact Manager row display: CRM `notes`, then `Notes`, then legacy import `Comments`. */
export function recipientNoteText(row) {
  if (!row || typeof row !== 'object') return '';
  const n = clean(row.notes || row.Notes || '');
  if (n) return n;
  return clean(row.Comments || row.comments || '');
}

/**
 * Plain text for CSV export: primary `notes` / `Notes`, plus legacy import columns if they still hold text
 * (LeadsExport-style Comments / Comments 2 that were not merged into `notes` on older rows).
 */
export function recipientInternalNotesForExport(row) {
  const primary = recipientNoteText(row);
  const c1 = clean(row.Comments || row.comments || '');
  const c2 = clean(row['Comments 2'] || row['comments 2'] || row.comments_2 || '');
  const parts = [];
  if (primary) parts.push(primary);
  if (c1 && c1 !== primary) parts.push(c1);
  if (c2 && c2 !== primary && c2 !== c1) parts.push(c2);
  return parts.join('\n\n');
}

/**
 * JSON for Contact Manager CSV column `Stage history (JSON)` so file merge can round-trip pipeline
 * when the flat `Lead Status` cell is still `Lead` (same as CRM display in some cases).
 * Empty when there is no history.
 */
export function serializeStageHistoryForCsvExport(stageHistory) {
  if (!Array.isArray(stageHistory) || !stageHistory.length) return '';
  try {
    const slim = stageHistory
      .map((e) => {
        if (!e || typeof e !== 'object') return null;
        const from = e.from ?? e.From ?? '';
        const to = e.to ?? e.To ?? '';
        let at = '';
        if (typeof e.at === 'string') at = e.at;
        else if (e.at instanceof Date) at = e.at.toISOString();
        else if (typeof e.at?.toDate === 'function') {
          try {
            const d = e.at.toDate();
            at = d instanceof Date ? d.toISOString() : '';
          } catch {
            at = '';
          }
        } else if (e.at && typeof e.at.seconds === 'number') {
          at = new Date(e.at.seconds * 1000).toISOString();
        }
        const by = e.by ?? e.By;
        const hop = { from: String(from), to: String(to), at };
        if (by !== undefined && by !== null && String(by)) hop.by = String(by);
        return hop;
      })
      .filter(Boolean);
    if (!slim.length) return '';
    return JSON.stringify(slim);
  } catch {
    return '';
  }
}

/**
 * When merging two recipient rows for the same email, avoid wiping notes with an explicit empty
 * `notes` / `Notes` on the later row (e.g. Active DB row after a list row, or a second list).
 *
 * @param {{ emptyPrimaryMeansClear?: boolean }} [options] When true (server baseline merge only),
 * `notes: ""` on the incoming row with no legacy body clears CRM text instead of resurrecting the server copy.
 */
export function coalesceNotesForEmailDedupe(prev, next, options = {}) {
  if (!next || typeof next !== 'object') {
    return recipientNoteText(prev);
  }
  const p = recipientNoteText(prev);
  const n = recipientNoteText(next);
  if (
    options.emptyPrimaryMeansClear &&
    Object.prototype.hasOwnProperty.call(next, 'notes') &&
    !clean(next.notes) &&
    !n
  ) {
    return '';
  }
  const nextOwnsNotesKey =
    Object.prototype.hasOwnProperty.call(next, 'notes') ||
    Object.prototype.hasOwnProperty.call(next, 'Notes') ||
    Object.prototype.hasOwnProperty.call(next, 'Comments') ||
    Object.prototype.hasOwnProperty.call(next, 'comments');
  if (p && n) {
    if (p === n) return p;
    // Prefer the longer string when one is a prefix of the other (keeps post-import CRM text vs stale short rows)
    if (p.startsWith(n)) return p.length >= n.length ? p : n;
    if (n.startsWith(p)) return n.length >= p.length ? n : p;
    if (n.length > p.length) return n;
    if (p.length > n.length) return p;
    return n;
  }
  if (n) return n;
  if (nextOwnsNotesKey && p) return p;
  return n || p;
}

/**
 * Before persisting a lead-stage change, keep Firestore text unless the UI row has a longer note
 * (e.g. merged ALL Contacts view) or the list row had none.
 */
export function bestRecipientNotesForStageSave(listRow, uiRef) {
  if (!uiRef || typeof uiRef !== 'object' || !listRow || typeof listRow !== 'object') return listRow;
  const lt = recipientNoteText(listRow);
  const ut = recipientNoteText(uiRef);
  if (!ut) return listRow;
  if (!lt || ut.length > lt.length) {
    const { Notes: _dropN, ...rest } = listRow;
    return { ...rest, notes: ut };
  }
  return listRow;
}

/**
 * Contact Manager can show account holders still on a Lead* stage as "Won (Account)" in the table
 * (`withAccountOverlay`). If a save payload carries that display value without a matching
 * `stageHistory` advance vs Firestore, treat it as a UI artifact and keep the server pipeline fields.
 */
function shouldRevertWonAccountLeadOverlay(srv, cr) {
  const s = clean(srv?.leadStatus || '');
  const c = clean(cr?.leadStatus || '');
  if (!s || c !== 'Won (Account)') return false;
  if (!/^lead/i.test(s)) return false;
  const shCr = Array.isArray(cr?.stageHistory) ? cr.stageHistory.length : 0;
  const shSrv = Array.isArray(srv?.stageHistory) ? srv.stageHistory.length : 0;
  if (shCr > shSrv) return false;
  if (shCr > 0 && shSrv > 0 && shCr === shSrv) {
    const lastCr = cr.stageHistory[shCr - 1];
    if (lastCr && clean(String(lastCr.to || '')) === 'Won (Account)') return false;
  }
  return true;
}

/** Last pipeline transition target on a row (`stageHistory[-1].to`), normalised. */
function lastStageHistoryToId(recipient) {
  return lastPipelineTargetFromStageHistory(recipient?.stageHistory);
}

function pickLeadStageHistoryForBaselineMerge(srv, cr, merged) {
  if (shouldRevertWonAccountLeadOverlay(srv, cr)) {
    return { leadStatus: srv.leadStatus, stageHistory: srv.stageHistory };
  }
  const crHist = Array.isArray(cr?.stageHistory) ? cr.stageHistory.length : 0;
  const srvHist = Array.isArray(srv?.stageHistory) ? srv.stageHistory.length : 0;
  if (crHist > srvHist) {
    return { leadStatus: cr.leadStatus, stageHistory: cr.stageHistory };
  }
  const cLead = clean(cr?.leadStatus || '');
  const sLead = clean(srv?.leadStatus || '');
  if (cLead !== sLead) {
    const crTail = lastStageHistoryToId(cr);
    const srvTail = lastStageHistoryToId(srv);
    // Save payload from recipientWithStageChange: leadStatus matches the last history hop.
    if (crTail && crTail === cLead) {
      return { leadStatus: cr.leadStatus, stageHistory: cr.stageHistory };
    }
    // Server strictly ahead on history depth and self-consistent — keep server pipeline fields.
    if (srvHist > crHist && srvTail && srvTail === sLead) {
      return { leadStatus: srv.leadStatus, stageHistory: srv.stageHistory };
    }
    return { leadStatus: srv.leadStatus, stageHistory: srv.stageHistory };
  }
  return { leadStatus: merged.leadStatus, stageHistory: merged.stageHistory };
}

/**
 * For each row in `incomingRecipients` (full list the UI intends to save), merge with the
 * matching Firestore row by `id` then email so keys only present on the server are not dropped
 * when the client array was built from a slightly stale snapshot.
 */
export function mergeIncomingRecipientsWithServerBaseline(serverRecipients, incomingRecipients) {
  if (!Array.isArray(incomingRecipients)) return [];
  if (!Array.isArray(serverRecipients) || serverRecipients.length === 0) {
    return incomingRecipients;
  }
  const findServer = (cr) => {
    if (!cr || typeof cr !== 'object') return null;
    if (cr.id) {
      const byId = serverRecipients.find((sr) => sr && sr.id === cr.id);
      if (byId) return byId;
    }
    const em = toLower(cr.email || cr.Email || '');
    if (!em) return null;
    return serverRecipients.find((sr) => sr && toLower(sr.email || sr.Email || '') === em) || null;
  };
  return incomingRecipients.map((cr) => {
    const srv = findServer(cr);
    if (!srv) return cr;
    const merged = { ...srv, ...cr };
    const notePick = coalesceNotesForEmailDedupe(srv, cr, { emptyPrimaryMeansClear: true });
    delete merged.Notes;
    if (notePick) merged.notes = notePick;
    else delete merged.notes;
    const lh = pickLeadStageHistoryForBaselineMerge(srv, cr, merged);
    merged.leadStatus = lh.leadStatus;
    merged.stageHistory = lh.stageHistory;
    return merged;
  });
}

/**
 * Fold legacy import columns into a single `notes` string and drop duplicate keys so Firestore has
 * one source of truth (London Smoke & Cure CRM saves).
 */
export function foldLegacyNotesColumnsIntoSingleNotesField(recipient) {
  if (!recipient || typeof recipient !== 'object') return recipient;
  const out = { ...recipient };
  const folded = recipientInternalNotesForExport(out).trim();
  if (folded) out.notes = folded;
  else delete out.notes;
  delete out.Notes;
  delete out.Comments;
  delete out.comments;
  delete out['Comments 2'];
  delete out['comments 2'];
  delete out.comments_2;
  return out;
}

/**
 * Normalise `recipients[]` for a Firestore list write: baseline-merge with server rows, optional
 * company chip auto-tag, then {@link stripUndefinedDeep}. Shared by Contact Manager
 * `saveRecipientsToList` and {@link commitListRecipientsFirestoreTransactional}.
 *
 * @param {object} listData Firestore list document fields (uses `settings`).
 * @param {object[]} serverRecipients Latest `recipients` from Firestore (may be empty).
 * @param {object[]} clientRows Intended full `recipients` array from the UI.
 */
export function buildRecipientsToSaveForListDoc(listData, serverRecipients, clientRows) {
  const data = listData && typeof listData === 'object' ? listData : {};
  const settings = data.settings || { autoTagCompany: true, autoTagCityFromMaps: true };
  const srv = Array.isArray(serverRecipients) ? serverRecipients : [];
  const client = Array.isArray(clientRows) ? clientRows : [];
  let recipientsToSave =
    srv.length && client.length
      ? mergeIncomingRecipientsWithServerBaseline(srv, client)
      : client;
  if (settings.autoTagCompany !== false) {
    recipientsToSave = recipientsToSave.map((recipient) => {
      const companyChip = extractCompanyChip(recipient.company || recipient['Company Name']);
      if (companyChip) {
        const chips = Array.from(new Set([...(recipient.chips || []), companyChip]));
        return { ...recipient, chips };
      }
      return recipient;
    });
  }
  return recipientsToSave.map((r) => stripUndefinedDeep(foldLegacyNotesColumnsIntoSingleNotesField(r)));
}

/**
 * Recipients present on the server list but not represented in the payload about to be written
 * (match by row `id`, else normalised email). Used to block stale full-array overwrites.
 */
export function findServerRecipientsMissingFromSave(serverRows, saveRows) {
  if (!Array.isArray(serverRows) || !serverRows.length || !Array.isArray(saveRows)) return [];
  const saveById = new Set(saveRows.map((r) => r && r.id).filter(Boolean));
  const saveByEmail = new Set(
    saveRows.map((r) => toLower(r?.email || r?.Email || '')).filter(Boolean),
  );
  const missing = [];
  for (const r of serverRows) {
    if (!r || typeof r !== 'object') continue;
    const id = r.id;
    const em = toLower(r.email || r.Email || '');
    if (!id && !em) continue;
    const stillThere = (id && saveById.has(id)) || (em && saveByEmail.has(em));
    if (!stillThere) missing.push(r);
  }
  return missing;
}

/**
 * Throws unless the user confirms, or `bypassRemovalGuard` is set (intentional bulk delete, peel, etc.).
 * Call with the server `recipients` snapshot and the exact array you are about to write.
 */
export function assertNoAccidentalRecipientRemoval(serverRows, saveRows, options = {}) {
  if (options.bypassRemovalGuard) return;
  const missing = findServerRecipientsMissingFromSave(serverRows, saveRows);
  if (!missing.length) return;
  const sample = missing
    .slice(0, 5)
    .map((r) => clean(r.email || r.Email || r.id || '?'))
    .filter(Boolean)
    .join(', ');
  const more = missing.length > 5 ? ` (+${missing.length - 5} more)` : '';
  const ok = window.confirm(
    `London Smoke & Cure — list save safety check\n\n` +
      `This write would REMOVE ${missing.length} contact(s) who are already on this list in Firestore but are missing from the copy you are saving.\n\n` +
      `That usually means this browser tab has an old list (another tab, Contact Manager, or Newsletter Editor changed the list).\n\n` +
      `Examples: ${sample}${more}\n\n` +
      `Cancel = no write (recommended). OK = write anyway and those contacts will be deleted from this list.`,
  );
  if (!ok) {
    throw new Error(
      'SAVE_ABORTED_REMOVAL_GUARD: Nothing was written. Refresh Contact Manager or Newsletter Editor, then try again.',
    );
  }
}

/**
 * Same normalised email appearing on more than one row in one `lists` document (import / scan quirks).
 * Contact Manager merges duplicates for display; operators should merge or delete extras in Firebase Console.
 *
 * @param {object[]} recipients
 * @returns {{ email: string, count: number, ids: string[] }[]}
 */
export function findDuplicateRecipientEmailsInList(recipients) {
  if (!Array.isArray(recipients) || !recipients.length) return [];
  const by = new Map();
  for (const r of recipients) {
    if (!r || typeof r !== 'object') continue;
    const em = toLower(r.email || r.Email || '');
    if (!em) continue;
    if (!by.has(em)) by.set(em, { count: 0, ids: [] });
    const slot = by.get(em);
    slot.count += 1;
    const id = r.id != null && String(r.id).trim() ? String(r.id).trim() : '';
    if (id && !slot.ids.includes(id)) slot.ids.push(id);
  }
  return [...by.entries()]
    .filter(([, v]) => v.count > 1)
    .map(([email, v]) => ({ email, count: v.count, ids: v.ids }));
}

/**
 * Merge recipient rows by normalised email: later rows overlay earlier. Chips are unioned by default;
 * notes use {@link coalesceNotesForEmailDedupe}. For **Import merge**, pass `{ uploadedTextWins: true }` so
 * incoming notes and chips replace server values for matched emails (upload is authoritative for text/tags).
 *
 * @param {object[]} existingRecipients
 * @param {object[]} incoming
 * @param {{ uploadedTextWins?: boolean }} [options]
 */
export function mergeByEmail(existingRecipients, incoming, options = {}) {
  const uploadedTextWins = !!(options && options.uploadedTextWins);
  const byEmail = new Map();
  for (const r of existingRecipients) {
    const em = toLower(r.email || r.Email);
    if (!em) continue;
    byEmail.set(em, { ...r });
  }
  for (const r of incoming) {
    const em = toLower(r.email || r.Email);
    if (!em) continue;
    if (byEmail.has(em)) {
      const prev = byEmail.get(em);
      const chips = uploadedTextWins
        ? Array.from(new Set([...(r.chips || [])].map(clean).filter(Boolean)))
        : Array.from(
            new Set([...(prev.chips || []), ...(r.chips || [])].map(clean).filter(Boolean)),
          );
      const noteText = uploadedTextWins
        ? recipientNoteText(r)
        : coalesceNotesForEmailDedupe(prev, r);
      const pLen = recipientNoteText(prev).length;
      const rLen = recipientNoteText(r).length;
      /** Prefer the Firestore row id that actually carries the richer CRM note so the table row matches one real sub-document. */
      let canonicalId = prev.id || r.id || `${em}-${uniqueId()}`;
      if (uploadedTextWins) {
        if (clean(r.id)) canonicalId = clean(r.id);
        else if (clean(prev.id)) canonicalId = clean(prev.id);
      } else {
        if (pLen > rLen) canonicalId = prev.id || r.id || canonicalId;
        else if (rLen > pLen) canonicalId = r.id || prev.id || canonicalId;
      }
      const merged = {
        ...prev,
        ...r,
        chips,
        id: canonicalId,
      };
      delete merged.Notes;
      if (uploadedTextWins) {
        delete merged.Comments;
        delete merged.comments;
        delete merged['Comments 2'];
        delete merged['comments 2'];
        delete merged.comments_2;
        const fileExplicit = resolveLeadStatusFromImportRow(r);
        const histTo = lastPipelineTargetFromStageHistory(r.stageHistory);
        const pipelineFromFile = clean(fileExplicit) || histTo || '';
        merged.leadStatus =
          pipelineFromFile || clean(prev.leadStatus) || clean(r.leadStatus) || 'Lead';
        if (Object.prototype.hasOwnProperty.call(r, 'stageHistory')) {
          merged.stageHistory = Array.isArray(r.stageHistory)
            ? r.stageHistory.map((e) => (e && typeof e === 'object' ? { ...e } : e))
            : r.stageHistory;
        } else if (clean(fileExplicit) && !histTo) {
          const prevSt = clean(prev.leadStatus || '');
          const nextSt = clean(merged.leadStatus || '');
          if (nextSt && nextSt !== prevSt) {
            merged.stageHistory = [
              {
                at: new Date().toISOString(),
                by: 'import:merge',
                from: prevSt || 'Lead',
                to: nextSt,
              },
            ];
          }
        }
      } else {
        merged.leadStatus = r.leadStatus || prev.leadStatus || 'Lead';
      }
      if (noteText) merged.notes = noteText;
      else {
        delete merged.notes;
      }
      byEmail.set(em, merged);
    } else {
      byEmail.set(em, r);
    }
  }
  return Array.from(byEmail.values()).map((row) => stripUndefinedDeep(row));
}

/**
 * Import merge: Firebase Console dumps (and some CSV exports) may list the same email more than once.
 * A **later** duplicate is often an older snapshot (still `Lead`) while an **earlier** block carries the
 * real pipeline (`Qualified`, `stageHistory`, longer notes). {@link mergeByEmail} alone keeps the last
 * row only, which can wipe stages. This helper keeps one row per normalised email, preferring the
 * richest pipeline row before merge.
 *
 * @param {object[]} rows normalised rows (e.g. after {@link normalizeRecipient})
 * @returns {object[]} stable order: first-seen email order, one row per email
 */
export function dedupeIncomingRecipientsPreferRicherPipeline(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  const order = [];
  const groups = new Map();
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue;
    const em = toLower(r.email || r.Email || '');
    if (!em) continue;
    if (!groups.has(em)) {
      order.push(em);
      groups.set(em, []);
    }
    groups.get(em).push(r);
  }
  const pickScore = (r) => {
    const sh = Array.isArray(r.stageHistory) ? r.stageHistory.length : 0;
    const fromHist = clean(lastPipelineTargetFromStageHistory(r.stageHistory) || '');
    const explicit = clean(resolveLeadStatusFromImportRow(r) || '');
    const rawLead = clean(r.leadStatus || r['Lead Status'] || '');
    const rawLow = rawLead.toLowerCase();
    const pipe =
      rawLow === 'lead' && fromHist
        ? fromHist
        : clean(explicit || fromHist || rawLead || '');
    const low = pipe.toLowerCase();
    const nonDefaultLead = low && low !== 'lead' ? 1_000_000 : 0;
    const noteLen = recipientNoteText(r).length;
    return nonDefaultLead + sh * 10_000 + noteLen;
  };
  return order.map((em) => {
    const g = groups.get(em) || [];
    if (g.length === 1) return g[0];
    return [...g].sort((a, b) => pickScore(b) - pickScore(a))[0];
  });
}

/**
 * Import merge preview: how many existing list rows will differ after {@link mergeByEmail} vs the
 * current server snapshot. Category counts can overlap one contact (notes plus tags, etc.).
 *
 * @param {object[]} currentServerRows from {@link fetchServerRecipientsRowsForList}
 * @param {object[]} incomingNorm normalized incoming rows (with email)
 * @param {object[]} merged result of `mergeByEmail(currentServerRows, incomingNorm, { uploadedTextWins: true })` for Import merge
 * @returns {{
 *   uniqueIncomingEmailCount: number,
 *   duplicateIncomingRowExtras: number,
 *   matchedOnServerByEmail: number,
 *   updatesAnySavedDifference: number,
 *   noopMatchedCount: number,
 *   updatesLeadStage: number,
 *   updatesNotes: number,
 *   updatesChipsOrTags: number,
 *   updatesNameCompanyPhoneAddress: number,
 * }}
 */
export function summarizeImportMergeChangeImpact(currentServerRows, incomingNorm, merged) {
  const beforeByEmail = new Map();
  for (const r of currentServerRows || []) {
    if (!r || typeof r !== 'object') continue;
    const em = toLower(r.email || r.Email);
    if (!em) continue;
    beforeByEmail.set(em, { ...r });
  }
  const afterByEmail = new Map();
  for (const r of merged || []) {
    if (!r || typeof r !== 'object') continue;
    const em = toLower(r.email || r.Email);
    if (!em) continue;
    afterByEmail.set(em, { ...r });
  }

  const uniqueIncomingEmails = new Set();
  let duplicateIncomingRowExtras = 0;
  for (const r of incomingNorm || []) {
    if (!r || typeof r !== 'object') continue;
    const em = toLower(r.email || r.Email);
    if (!em) continue;
    if (uniqueIncomingEmails.has(em)) duplicateIncomingRowExtras += 1;
    else uniqueIncomingEmails.add(em);
  }

  const normLead = (row) =>
    clean(
      resolveLeadStatusFromImportRow(row) ||
        lastPipelineTargetFromStageHistory(row?.stageHistory) ||
        row?.leadStatus ||
        row?.['Lead Status'] ||
        row?.status ||
        '',
    ) || 'Lead';

  const chipsSignature = (row) =>
    [...new Set((row?.chips || []).map((c) => clean(c)).filter(Boolean))].sort().join('\u0001');

  const profileComparable = (row) => ({
    company: clean(row?.company || row?.['Company Name'] || row?.Company || ''),
    firstName: clean(row?.firstName || row?.['First Name'] || ''),
    lastName: clean(row?.lastName || row?.['Last Name'] || ''),
    phone: clean(row?.phone || row?.Phone || row?.mobile || row?.Mobile || ''),
    address: clean(row?.address || row?.Address || ''),
    jobTitle: clean(row?.jobTitle || row?.['Job Title'] || ''),
    web: clean(row?.companyWebsite || row?.['Company Website'] || row?.website || row?.URL || row?.Url || ''),
    city: clean(row?.city || row?.City || ''),
  });

  let matchedOnServerByEmail = 0;
  let updatesAnySavedDifference = 0;
  let updatesLeadStage = 0;
  let updatesNotes = 0;
  let updatesChipsOrTags = 0;
  let updatesNameCompanyPhoneAddress = 0;

  for (const em of uniqueIncomingEmails) {
    if (!beforeByEmail.has(em)) continue;
    matchedOnServerByEmail += 1;
    const before = beforeByEmail.get(em);
    const after = afterByEmail.get(em);
    if (!after) continue;

    let anyDiff = false;
    if (normLead(before) !== normLead(after)) {
      updatesLeadStage += 1;
      anyDiff = true;
    }
    if (recipientNoteText(before) !== recipientNoteText(after)) {
      updatesNotes += 1;
      anyDiff = true;
    }
    if (chipsSignature(before) !== chipsSignature(after)) {
      updatesChipsOrTags += 1;
      anyDiff = true;
    }
    if (JSON.stringify(profileComparable(before)) !== JSON.stringify(profileComparable(after))) {
      updatesNameCompanyPhoneAddress += 1;
      anyDiff = true;
    }
    if (anyDiff) updatesAnySavedDifference += 1;
  }

  const noopMatchedCount = Math.max(0, matchedOnServerByEmail - updatesAnySavedDifference);

  return {
    uniqueIncomingEmailCount: uniqueIncomingEmails.size,
    duplicateIncomingRowExtras,
    matchedOnServerByEmail,
    updatesAnySavedDifference,
    noopMatchedCount,
    updatesLeadStage,
    updatesNotes,
    updatesChipsOrTags,
    updatesNameCompanyPhoneAddress,
  };
}

/**
 * When several Firestore lists are selected, {@link mergeByEmail} would merge every field for the
 * same email (later lists overwrite notes, phone, etc.). Staff then see CRM notes in the table
 * while the Firestore row on the list they inspect still has an empty `notes` field, and saves
 * from another tab can later feel like random data loss. This helper keeps **scalar row data**
 * (notes, pipeline, address, etc.) from one **primary** list and only **unions `chips`** from the
 * other selected lists. Primary = first selected id that is a normal writable Firestore list (not
 * Active DB, ALL Contacts prototype, or Marketing unsubscribes); if there is none, index `0`.
 *
 * Single-list selection delegates to {@link mergeByEmail} so duplicate emails inside one list doc
 * still coalesce the same way as before.
 *
 * @param {string[]} selectedListIds
 * @param {object[][]} perListRows parallel arrays: `perListRows[i]` = recipients for `selectedListIds[i]`
 * @returns {object[]}
 */
export function mergeRecipientsForMultiListView(selectedListIds, perListRows) {
  if (!Array.isArray(selectedListIds) || !selectedListIds.length) return [];
  if (!Array.isArray(perListRows) || !perListRows.length) return [];
  if (selectedListIds.length === 1) {
    const flat = [].concat(...perListRows);
    return mergeByEmail([], flat);
  }

  const primaryIdx = selectedListIds.findIndex(
    (id) =>
      id !== ACTIVE_DB_LIST_ID &&
      id !== ALL_CONTACTS_LIST_ID &&
      id !== MARKETING_UNSUBSCRIBES_LIST_ID,
  );
  const primary = primaryIdx >= 0 ? primaryIdx : 0;

  /** @type {Map<string, object>} */
  const byEmail = new Map();
  const order = [];

  const chipUnion = (a, b) =>
    Array.from(
      new Set([...(a || []), ...(b || [])].map((c) => clean(c)).filter(Boolean)),
    );

  const primaryRows = Array.isArray(perListRows[primary]) ? perListRows[primary] : [];
  for (const r of primaryRows) {
    if (!r || typeof r !== 'object') continue;
    const em = toLower(r.email || r.Email || '');
    if (!em) continue;
    if (!byEmail.has(em)) {
      byEmail.set(em, { ...r, chips: [...(r.chips || [])] });
      order.push(em);
    } else {
      const prev = byEmail.get(em);
      byEmail.set(em, { ...prev, chips: chipUnion(prev.chips, r.chips) });
    }
  }

  for (let i = 0; i < perListRows.length; i++) {
    if (i === primary) continue;
    const rows = Array.isArray(perListRows[i]) ? perListRows[i] : [];
    for (const r of rows) {
      if (!r || typeof r !== 'object') continue;
      const em = toLower(r.email || r.Email || '');
      if (!em) continue;
      if (byEmail.has(em)) {
        const prev = byEmail.get(em);
        byEmail.set(em, {
          ...prev,
          chips: chipUnion(prev.chips, r.chips),
        });
      } else {
        byEmail.set(em, { ...r, chips: [...(r.chips || [])] });
        order.push(em);
      }
    }
  }

  return order.map((em) => stripUndefinedDeep(byEmail.get(em)));
}

/**
 * Union of normalised emails for given Firestore list ids (real `lists` docs only).
 * @param {Array<{ id: string, recipients?: unknown[] }>} listsArray
 * @param {string[]} listIds
 * @returns {Set<string>}
 */
export function buildEmailSetFromLists(listsArray, listIds) {
  const out = new Set();
  if (!Array.isArray(listsArray) || !Array.isArray(listIds)) return out;
  const idSet = new Set(listIds.filter(Boolean));
  for (const list of listsArray) {
    if (!list || !idSet.has(list.id)) continue;
    for (const r of list.recipients || []) {
      if (!r || typeof r !== 'object') continue;
      const em = toLower(r.email || r.Email);
      if (em) out.add(em);
    }
  }
  return out;
}

/**
 * Intersection of normalised emails across lists (contact must appear on every selected list).
 * @param {Array<{ id: string, recipients?: unknown[] }>} listsArray
 * @param {string[]} listIds
 * @returns {Set<string>}
 */
export function buildEmailIntersectionFromLists(listsArray, listIds) {
  const ids = (listIds || []).filter(Boolean);
  if (!ids.length || !Array.isArray(listsArray)) return new Set();
  const sets = ids.map((id) => {
    const list = listsArray.find((l) => l && l.id === id);
    const s = new Set();
    for (const r of list?.recipients || []) {
      if (!r || typeof r !== 'object') continue;
      const em = toLower(r.email || r.Email);
      if (em) s.add(em);
    }
    return s;
  });
  let acc = new Set(sets[0] || []);
  for (let i = 1; i < sets.length; i++) {
    acc = new Set([...acc].filter((e) => sets[i].has(e)));
  }
  return acc;
}

/**
 * Keep rows whose email is in `requiredSet`; others go to `outside`.
 * @param {object[]} rows
 * @param {Set<string>} requiredSet
 * @returns {{ keep: object[], outside: object[] }}
 */
export function partitionRequireListMembership(rows, requiredSet) {
  const keep = [];
  const outside = [];
  if (!requiredSet || requiredSet.size === 0) {
    return { keep: [...(rows || [])], outside: [] };
  }
  for (const r of rows || []) {
    const em = toLower(r?.email || r?.Email || '');
    if (em && requiredSet.has(em)) keep.push(r);
    else outside.push(r);
  }
  return { keep, outside };
}

/**
 * Split rows by whether their email appears in `emailSet` (lowercased).
 * Rows with no email are kept (cannot match set).
 * @param {object[]} rows
 * @param {Set<string>} emailSet
 * @returns {{ keep: object[], overlap: object[] }}
 */
export function partitionByListMembership(rows, emailSet) {
  const keep = [];
  const overlap = [];
  if (!emailSet || emailSet.size === 0) {
    return { keep: [...(rows || [])], overlap: [] };
  }
  for (const r of rows || []) {
    const em = toLower(r?.email || r?.Email || '');
    if (!em) {
      keep.push(r);
      continue;
    }
    if (emailSet.has(em)) overlap.push(r);
    else keep.push(r);
  }
  return { keep, overlap };
}

function cloneRecipientRowForExport(r) {
  if (!r || typeof r !== 'object') return null;
  const row = { ...r };
  if ('listId' in row) delete row.listId;
  if ('listName' in row) delete row.listName;
  return row;
}

/**
 * Normalised list bundle for Venn overlap (same shape as computeListEmailRegions inner prep).
 * @param {Array<{ id: string, name?: string, recipients?: unknown[] }>} listsInput
 */
function normaliseListsForVenn(listsInput) {
  return (listsInput || [])
    .filter((l) => l && l.id)
    .map((l) => ({
      id: l.id,
      name: clean(l.name || l.id),
      recipients: l.recipients || [],
      set: new Set(
        (l.recipients || [])
          .map((r) => toLower(r?.email || r?.Email || ''))
          .filter(Boolean),
      ),
    }));
}

/**
 * Build recipient rows for one Venn region. When an email exists in several lists (overlap),
 * use the row from `seedListIndex` when that list contains the email; otherwise lowest list index.
 * @param {Array<{ id: string, name?: string, recipients?: unknown[] }>} listsInput
 * @param {number} mask — region bitmask (same as computeListEmailRegions)
 * @param {number} [seedListIndex=0] — which selected list wins for merged emails (0-based, sidebar order)
 * @returns {object[]}
 */
export function buildVennRegionRecipients(listsInput, mask, seedListIndex = 0) {
  const lists = normaliseListsForVenn(listsInput);
  const n = lists.length;
  if (!n || !mask) return [];

  const emailToMask = new Map();
  for (let i = 0; i < n; i++) {
    for (const e of lists[i].set) {
      emailToMask.set(e, (emailToMask.get(e) || 0) | (1 << i));
    }
  }

  /** @type {Map<string, object>[]} */
  const emailToRowByList = lists.map((list) => {
    const m = new Map();
    for (const r of list.recipients) {
      const e = toLower(r?.email || r?.Email || '');
      if (!e) continue;
      if (!m.has(e)) m.set(e, cloneRecipientRowForExport(r));
    }
    return m;
  });

  const seed =
    Number.isInteger(seedListIndex) && seedListIndex >= 0 && seedListIndex < n ? seedListIndex : 0;

  const pickRow = (email, eMask) => {
    const inIdx = [];
    for (let i = 0; i < n; i++) {
      if (eMask & (1 << i)) inIdx.push(i);
    }
    if (inIdx.length === 0) return null;
    if (inIdx.length === 1) {
      return emailToRowByList[inIdx[0]].get(email) || null;
    }
    if ((eMask & (1 << seed)) && emailToRowByList[seed].has(email)) {
      return emailToRowByList[seed].get(email);
    }
    for (const i of inIdx.sort((a, b) => a - b)) {
      const row = emailToRowByList[i].get(email);
      if (row) return row;
    }
    return null;
  };

  const recipients = [];
  for (const [e, eMask] of emailToMask) {
    if (eMask !== mask) continue;
    const row = pickRow(e, eMask);
    if (row) recipients.push(row);
  }
  recipients.sort((a, b) =>
    toLower(a?.email || a?.Email || '').localeCompare(toLower(b?.email || b?.Email || '')),
  );
  return recipients;
}

/**
 * Multi-list email overlap by bitmask region (Venn-style analysis).
 * Indices follow `listsInput` order: bit i ⇒ email present in `listsInput[i]`.
 * @param {Array<{ id: string, name?: string, recipients?: unknown[] }>} listsInput
 * @returns {{
 *   n: number,
 *   lists: Array<{ id: string, name: string, count: number, exclusiveCount: number }>,
 *   regions: Array<{ mask: number, count: number, listIndexes: number[], recipients: object[] }>,
 *   unionSize: number,
 *   inAllLists: number,
 * }}
 */
export function computeListEmailRegions(listsInput) {
  const lists = normaliseListsForVenn(listsInput);
  const n = lists.length;
  if (n === 0) {
    return { n: 0, lists: [], regions: [], unionSize: 0, inAllLists: 0 };
  }

  const emailToMask = new Map();
  for (let i = 0; i < n; i++) {
    for (const e of lists[i].set) {
      emailToMask.set(e, (emailToMask.get(e) || 0) | (1 << i));
    }
  }

  const regionCounts = new Map();
  for (const mask of emailToMask.values()) {
    regionCounts.set(mask, (regionCounts.get(mask) || 0) + 1);
  }

  const fullMask = (1 << n) - 1;
  const inAllLists = regionCounts.get(fullMask) || 0;

  const regions = [];
  for (let mask = 1; mask <= fullMask; mask++) {
    const count = regionCounts.get(mask) || 0;
    if (count === 0) continue;
    const listIndexes = [];
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) listIndexes.push(i);
    }
    const recipients = buildVennRegionRecipients(listsInput, mask, 0);
    regions.push({ mask, count, listIndexes, recipients });
  }
  regions.sort((a, b) => b.count - a.count);

  const listsOut = lists.map((l, idx) => {
    const onlyMask = 1 << idx;
    const exclusiveCount = regionCounts.get(onlyMask) || 0;
    return { id: l.id, name: l.name, count: l.set.size, exclusiveCount };
  });

  return {
    n,
    lists: listsOut,
    regions,
    unionSize: emailToMask.size,
    inAllLists,
  };
}

export function suggestUsername(companyName) {
  const base = (companyName || '').toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();
  const jam = base.replace(/\s+/g, '');
  const letters = (jam.match(/[A-Z]/g) || []).join('');
  const prefix = (letters + 'XXXX').slice(0, 4);
  return `${prefix}001`;
}

/**
 * Random temporary password for Contact Manager promote (and aligned admin flows).
 * Meets typical Firebase password rules (length + mixed character classes). Not stored in Firestore — only set on Firebase Auth.
 */
export function generatePromoteTempPassword() {
  return Math.random().toString(36).slice(-8) + 'A1!';
}

/** Best-effort UK postcode from a single-line address string. */
function extractUkPostCodeFromLine(addressStr) {
  const m = String(addressStr || '').match(/\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/i);
  if (!m) return '';
  return m[1].toUpperCase().replace(/\s+/g, ' ').trim();
}

/**
 * Parse comma-separated UK address (trade application snapshot) into Firestore fields.
 * e.g. "Orchards Farm Shop Ltd, Castle Fruit Farm, Newent, Gloucestershire, GL18 1LS"
 *   → line1 Castle Fruit Farm, city Newent, line3 Gloucestershire, postCode GL18 1LS
 * @param {string} line
 * @param {{ companyName?: string }} [opts]
 */
export function parseUkSingleLineAddress(line, opts = {}) {
  const raw = clean(line);
  if (!raw) {
    return { line1: '', line2: '', line3: '', city: '', postCode: '' };
  }

  const postCode = extractUkPostCodeFromLine(raw);
  let remainder = raw;
  if (postCode) {
    const re = new RegExp(postCode.replace(/\s/g, '\\s*'), 'i');
    remainder = remainder.replace(re, '').replace(/,\s*$/, '').trim();
  }

  let parts = remainder
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  const companyNorm = clean(opts.companyName).toLowerCase();
  if (companyNorm && parts.length > 1 && parts[0].toLowerCase() === companyNorm) {
    parts = parts.slice(1);
  }

  if (!parts.length) {
    return { line1: '', line2: '', line3: '', city: '', postCode };
  }
  if (parts.length === 1) {
    return { line1: parts[0], line2: '', line3: '', city: '', postCode };
  }
  if (parts.length === 2) {
    return { line1: parts[0], line2: '', line3: '', city: parts[1], postCode };
  }

  const county = parts[parts.length - 1];
  const city = parts[parts.length - 2];
  const line1 = parts.slice(0, -2).join(', ');
  return { line1, line2: '', line3: county, city, postCode };
}

/** Split primary contact name into first / last (same logic as Cloud Run buildRecipientFromBody). */
function splitPrimaryContactName(name) {
  const pn = clean(name);
  if (!pn) return { first: '', last: '' };
  const space = pn.indexOf(' ');
  if (space === -1) return { first: pn, last: '' };
  return { first: pn.slice(0, space).trim(), last: pn.slice(space + 1).trim() };
}

/** Best-effort address fields from list recipient / import row (various column names). */
function pickAddressFieldsFromContact(rec) {
  if (!rec || typeof rec !== 'object') {
    return { line1: '', line2: '', line3: '', city: '', postCode: '' };
  }
  const line1 =
    clean(rec.line1) ||
    clean(rec['Line 1']) ||
    clean(rec['Address line 1']) ||
    clean(rec.address) ||
    clean(rec.Address) ||
    clean(rec.street) ||
    '';
  const line2 =
    clean(rec.line2) || clean(rec['Line 2']) || clean(rec['Address line 2']) || clean(rec.address2) || '';
  const line3 = clean(rec.line3) || clean(rec['Line 3']) || '';
  const city = clean(rec.city) || clean(rec.City) || clean(rec.town) || clean(rec.region) || '';
  const postCode =
    clean(rec.postCode) ||
    clean(rec.postcode) ||
    clean(rec['Post Code']) ||
    clean(rec.Postcode) ||
    clean(rec.zip) ||
    '';
  return { line1, line2, line3, city, postCode };
}

export function buildUserProfileFromContact(rec) {
  const snap = rec?.accountFormSnapshot;
  const snapBiz = snap && typeof snap === 'object' ? clean(snap.businessAddress) : '';
  const snapDel = snap && typeof snap === 'object' ? clean(snap.deliveryAddress) : '';
  const snapAcPhone =
    snap && typeof snap === 'object' && snap.accountsContact && typeof snap.accountsContact === 'object'
      ? clean(snap.accountsContact.phone)
      : '';

  const merged = { ...rec };
  if (snapBiz && !clean(merged.address) && !clean(merged.Address)) {
    merged.address = snapBiz;
    merged.Address = snapBiz;
  }
  const primPhone = clean(merged.phone) || clean(merged.Phone) || clean(merged.tel) || clean(merged.mobile) || '';
  if (!primPhone && snapAcPhone) {
    merged.phone = snapAcPhone;
  }

  const snapPrimaryName =
    snap && typeof snap === 'object' ? clean(snap.primaryContactName) : '';
  if (!clean(merged.firstName) && !clean(merged['First Name']) && snapPrimaryName) {
    const { first, last } = splitPrimaryContactName(snapPrimaryName);
    if (first) merged.firstName = first;
    if (last) merged.lastName = last;
    if (first) merged['First Name'] = first;
    if (last) merged['Last Name'] = last;
  }

  const companyName = merged.company || merged['Company Name'] || '';
  const fullName = [merged.firstName || merged['First Name'] || '', merged.lastName || merged['Last Name'] || '']
    .filter(Boolean)
    .join(' ')
    .trim() || snapPrimaryName || companyName;

  const addr = pickAddressFieldsFromContact(merged);
  const line1Default = addr.line1 || companyName || '';
  let billingAddress;
  if (snapBiz) {
    billingAddress = parseUkSingleLineAddress(snapBiz, { companyName });
  } else {
    billingAddress = {
      line1: line1Default,
      line2: addr.line2,
      line3: addr.line3,
      city: addr.city,
      postCode: addr.postCode || extractUkPostCodeFromLine(addr.line1),
    };
  }
  let shippingAddress = { ...billingAddress };
  if (snapDel && snapDel !== snapBiz) {
    shippingAddress = parseUkSingleLineAddress(snapDel, { companyName });
  }

  return {
    Pricing: 'A',
    active: true,
    billingAddress,
    shippingAddress,
    companyName,
    contactInfo: clean(merged.phone) || clean(merged.tel) || clean(merged.mobile) || '',
    phone: clean(merged.phone) || clean(merged.tel) || clean(merged.mobile) || '',
    createdAt: new Date().toISOString(),
    email: merged.email || merged.Email || '',
    fullName: fullName || companyName || '',
    packingSlipNote: '',
    poSystem: '',
    role: 'Customer',
    username: '',
    windowEnd: '',
    windowStart: '',
  };
}

/** Trade application payload stored on `lists/account_requests` rows by Cloud Run (`accountFormSnapshot`). */
export function recipientHasAccountFormSnapshot(rec) {
  const s = rec?.accountFormSnapshot;
  return s != null && typeof s === 'object';
}

/**
 * Label + value rows for read-only display (Contact Manager modals).
 * @param {object} rec
 * @returns {{ label: string, value: string }[]}
 */
export function getAccountFormSnapshotFieldRows(rec) {
  if (!recipientHasAccountFormSnapshot(rec)) return [];
  const s = rec.accountFormSnapshot;
  const rows = [];
  const push = (label, val) => {
    if (val == null) return;
    const v = typeof val === 'object' ? clean(JSON.stringify(val)) : clean(String(val));
    if (!v) return;
    rows.push({ label, value: v });
  };
  push('Submitted at', s.submittedAt);
  push('Business address', s.businessAddress);
  push('Delivery address', s.deliveryAddress);
  push('Primary contact name', s.primaryContactName);
  const ac = s.accountsContact && typeof s.accountsContact === 'object' ? s.accountsContact : {};
  push('Accounts contact name', ac.name);
  push('Accounts contact email', ac.email);
  push('Accounts contact phone', ac.phone);
  push('Business type', s.businessType);
  push('Company number', s.companyNumber);
  push('VAT number', s.vatNumber);
  push('Credit limit requested', s.creditLimitRequested);
  push('Trade reference 1', s.tradeReference1);
  push('Trade reference 2', s.tradeReference2);
  const b = s.bank && typeof s.bank === 'object' ? s.bank : {};
  push('Bank name', b.bankName);
  push('Sort code', b.sortCode);
  push('Account number', b.accountNumber);
  return rows;
}

/** Stored on `lists/{id}`: optional exclusivity hints (not enforced server-side in this repo). */
export const LIST_KIND_STANDARD = 'standard';
export const LIST_KIND_SUPPRESSION = 'suppression';

/**
 * @param {unknown} raw
 * @returns {{ exclusionListIds: string[], enforcement?: 'off' | 'warn' }}
 */
export function normalizeListPolicy(raw) {
  if (!raw || typeof raw !== 'object') {
    return { exclusionListIds: [], enforcement: 'off' };
  }
  const ids = Array.isArray(raw.exclusionListIds)
    ? raw.exclusionListIds.map((x) => String(x || '').trim()).filter(Boolean)
    : [];
  const enforcement = raw.enforcement === 'warn' ? 'warn' : 'off';
  return { exclusionListIds: [...new Set(ids)], enforcement };
}

/**
 * Emails from `recipients` that also appear on any list in `exclusionListIds`.
 * @param {object[]} recipients
 * @param {string[]} exclusionListIds
 * @param {Array<{ id: string, recipients?: unknown[] }>} listsArray
 * @returns {{ overlapCount: number, byListId: Record<string, number> }}
 */
export function countRecipientsOverlappingPolicyLists(recipients, exclusionListIds, listsArray) {
  const policy = normalizeListPolicy({ exclusionListIds: exclusionListIds || [] });
  if (!policy.exclusionListIds.length || !Array.isArray(recipients)) {
    return { overlapCount: 0, byListId: {} };
  }
  const sets = policy.exclusionListIds.map((listId) => {
    const list = (listsArray || []).find((l) => l && l.id === listId);
    const s = new Set();
    for (const r of list?.recipients || []) {
      const em = toLower(r?.email || r?.Email || '');
      if (em) s.add(em);
    }
    return { listId, set: s };
  });
  const byListId = {};
  let overlapCount = 0;
  const seen = new Set();
  for (const r of recipients) {
    const em = toLower(r?.email || r?.Email || '');
    if (!em) continue;
    for (const { listId, set } of sets) {
      if (set.has(em)) {
        byListId[listId] = (byListId[listId] || 0) + 1;
        if (!seen.has(em)) {
          seen.add(em);
          overlapCount += 1;
        }
        break;
      }
    }
  }
  return { overlapCount, byListId };
}

/**
 * Human-readable summary of Contact Manager table filters (for governance strip and list lineage).
 * @param {{
 *   search?: string,
 *   includeTagGroups?: string[][],
 *   excludedChips?: string[],
 *   includedListIds?: string[],
 *   includeListsMode?: string,
 *   excludedListIds?: string[],
 *   accountStatusFilter?: string,
 *   leadStageViewFilter?: string,
 *   domainQuickFilter?: string,
 * }} filters
 * @param {Array<{ id: string, name?: string }>} listsForNames
 * @returns {string}
 */
export function buildContactManagerFilterSummary(filters, listsForNames) {
  const parts = [];
  const nameOf = (id) => {
    const row = (listsForNames || []).find((l) => l && l.id === id);
    return clean(row?.name || id);
  };

  const af = filters?.accountStatusFilter || 'all';
  if (af && af !== 'all') {
    parts.push(`Account: ${af}`);
  }
  const stage = clean(filters?.leadStageViewFilter || '');
  if (stage) parts.push(`Stage: ${stage}`);
  const dom = clean(filters?.domainQuickFilter || '');
  if (dom) parts.push(`Email / name contains: ${dom}`);
  const q = clean(filters?.search || '');
  if (q) parts.push(`Search: ${q}`);

  const steps = (filters?.includeTagGroups || []).filter((g) => Array.isArray(g) && g.length);
  if (steps.length) {
    parts.push(
      `Include tags (${steps.length} step(s), AND between steps): ${steps
        .map((g) => `(${g.join(' OR ')})`)
        .join(' AND ')}`,
    );
  }
  const ex = filters?.excludedChips || [];
  if (ex.length) parts.push(`Exclude tags: ${ex.join(', ')}`);

  const inc = (filters?.includedListIds || []).filter(Boolean);
  if (inc.length) {
    const mode = filters?.includeListsMode === 'all' ? 'all of' : 'any of';
    parts.push(`Include lists (${mode}): ${inc.map(nameOf).join(', ')}`);
  }
  const excLists = (filters?.excludedListIds || []).filter(Boolean);
  if (excLists.length) {
    parts.push(`Exclude lists: ${excLists.map(nameOf).join(', ')}`);
  }

  const tradeLine = formatTradeFilterSummary(filters);
  if (tradeLine) parts.push(tradeLine);

  if (!parts.length) return 'No table filters (showing combined list rows only).';
  return parts.join(' · ');
}

/**
 * Short human-readable lineage for list governance UI.
 * @param {unknown} lineage
 * @param {Array<{ id: string, name?: string }>} listsForNames
 * @returns {string}
 */
export function formatContactManagerLineageBrief(lineage, listsForNames) {
  if (!lineage || typeof lineage !== 'object') return '';
  const nameOf = (id) => {
    const row = (listsForNames || []).find((l) => l && l.id === id);
    return clean(row?.name || id);
  };
  const parts = [];
  if (lineage.kind) parts.push(`Created as: ${String(lineage.kind)}`);
  if (Array.isArray(lineage.parentListIds) && lineage.parentListIds.length) {
    parts.push(`Parent lists: ${lineage.parentListIds.map(nameOf).join(', ')}`);
  }
  if (clean(lineage.regionLabel || '')) parts.push(`Region: ${clean(lineage.regionLabel)}`);
  const fs = clean(lineage.filterSummary || '');
  if (fs) parts.push(`Saved filters: ${fs}`);
  const seed = clean(lineage.seedListId || '');
  if (seed) parts.push(`Seed list: ${nameOf(seed)}`);
  if (lineage.sourceListId || lineage.duplicatedFromListId) {
    parts.push(`From list: ${nameOf(lineage.sourceListId || lineage.duplicatedFromListId)}`);
  }
  return parts.join(' · ');
}
